import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useGlobalDataRefresh from "hooks/useGlobalDataRefresh";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftBadge from "components/SoftBadge";
import SoftPagination from "components/SoftPagination";

import { useAuth } from "context/AuthContext";
import { getTasks, getActions, updateTask, getMissionById, getAirports } from "./Users/services/graphqlService";
import {
  extractTasksFromServiceResponse,
  buildTaskDescription,
  formatTaskAge,
  getTaskCategoryPills,
  getTaskCreatedTime,
  sortTasksByNewest,
  taskMissionId,
  taskId,
  canDecideTask,
  taskStatus,
  prettifyLabel,
  shouldShowTaskStatusBadge,
  isPilotInterestTask,
  buildUpdateTaskInput,
  TASK_LIST_TABS,
  TASK_LIST_TAB_KEYS,
  taskMatchesStatusTab,
  extractLegsFromTask,
  extractLegIdFromTask,
  normalizeMissionLegs,
  legsHaveRouteDetails,
} from "./tasksParse";
import {
  extractPilotProfileIdFromTask,
  extractPilotDisplayNameFromTask,
  resolvePilotProfileFromTask,
} from "./pilotProfileDisplay";
import ViewPilotProfileModal from "./components/ViewPilotProfileModal";

const TASKS_PAGE_SIZE = 10;

const PILL_BADGE_COLORS = {
  primary: "info",
  secondary: "warning",
};

function CategoryPill({ label, tone = "primary" }) {
  return (
    <SoftBadge
      variant="contained"
      color={PILL_BADGE_COLORS[tone] || PILL_BADGE_COLORS.primary}
      badgeContent={label}
      size="xs"
      container
    />
  );
}

function TaskRowMeta({ age, pills, statusLabel }) {
  const statusLower = String(statusLabel ?? "").toLowerCase();
  const showStatusBadge = shouldShowTaskStatusBadge(statusLabel);
  const statusColor =
    statusLower === "accepted" || statusLower === "approved"
      ? "success"
      : statusLower === "denied" || statusLower === "rejected"
        ? "error"
        : "secondary";

  return (
    <SoftBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
      {age ? (
        <SoftBox display="flex" alignItems="center" gap={0.5}>
          <Icon sx={{ fontSize: "0.875rem !important", color: "text.secondary" }}>
            schedule
          </Icon>
          <SoftTypography variant="caption" color="secondary" fontWeight="medium">
            {age}
          </SoftTypography>
        </SoftBox>
      ) : null}
      {showStatusBadge ? (
        <SoftBadge
          variant="contained"
          color={statusColor}
          badgeContent={prettifyLabel(statusLabel)}
          size="xs"
          container
        />
      ) : null}
      {pills.map((pill) => (
        <CategoryPill
          key={`${pill.label}-${pill.tone}`}
          label={pill.label}
          tone={pill.tone}
        />
      ))}
    </SoftBox>
  );
}

function TaskActionButton({
  label,
  icon,
  color = "dark",
  variant = "outlined",
  disabled,
  onClick,
  loading,
}) {
  return (
    <SoftButton
      variant={variant}
      color={color}
      size="small"
      disabled={disabled || loading}
      onClick={onClick}
      sx={{
        minWidth: "auto",
        px: 1.25,
        py: 0.5,
        fontSize: "0.75rem",
        fontWeight: 600,
        borderRadius: "0.5rem",
        textTransform: "none",
        whiteSpace: "nowrap",
      }}
    >
      {loading ? (
        <CircularProgress size={14} color="inherit" />
      ) : (
        <>
          <Icon sx={{ fontSize: "1rem !important", mr: 0.35 }}>{icon}</Icon>
          {label}
        </>
      )}
    </SoftButton>
  );
}

function findTaskDecisionAction(actions, decision) {
  const list = Array.isArray(actions) ? actions : [];
  const pattern =
    decision === "accept"
      ? /accept|approve|acceptpilot/i
      : /deny|reject|decline|rejectpilot/i;

  return list.find((action) => {
    const haystack = [action?.type, action?.subType, action?.message, action?.label]
      .filter(Boolean)
      .join(" ");
    return pattern.test(haystack);
  });
}

function normalizeAirportsList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.AirportsList)) return raw.AirportsList;
  return [];
}

function getAirportLabel(airportId, airports) {
  if (!airportId) return "—";
  const id = String(airportId);
  const airport = airports.find(
    (a) => String(a?.airportId ?? a?.id ?? "") === id,
  );
  return airport?.name || airport?.code || id;
}

function TaskDecisionLegsList({
  legs,
  loading,
  airports,
  highlightLegId,
  interestedPilotName,
}) {
  if (loading) {
    return (
      <SoftBox display="flex" alignItems="center" gap={1} mt={2}>
        <CircularProgress size={16} color="info" />
        <SoftTypography variant="caption" color="secondary">
          Loading flight legs…
        </SoftTypography>
      </SoftBox>
    );
  }

  if (!legs?.length) return null;

  return (
    <SoftBox mt={2}>
      <SoftTypography variant="caption" fontWeight="bold" display="block" mb={1}>
        Flight legs
      </SoftTypography>
      <SoftBox
        sx={(theme) => ({
          borderRadius: "0.75rem",
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        })}
      >
        {legs.map((leg, index) => {
          const isHighlighted =
            Boolean(highlightLegId) &&
            leg.legId &&
            String(leg.legId) === String(highlightLegId);
          const isTargetLeg =
            isHighlighted || (legs.length === 1 && !highlightLegId);
          const route = `${getAirportLabel(leg.startAirport, airports)} → ${getAirportLabel(leg.endAirport, airports)}`;
          const pilotLabel =
            leg.pilotName ||
            (leg.pilotId ? leg.pilotId : null) ||
            (isTargetLeg && interestedPilotName ? interestedPilotName : null) ||
            "Unassigned";

          return (
            <SoftBox
              key={leg.legId || `leg-${index}`}
              px={2}
              py={1.25}
              display="flex"
              alignItems="flex-start"
              gap={1.5}
              sx={(theme) => ({
                borderTop:
                  index > 0 ? `1px solid ${theme.palette.divider}` : "none",
                backgroundColor: isTargetLeg
                  ? theme.palette.mode === "dark"
                    ? "rgba(99, 110, 255, 0.12)"
                    : "rgba(99, 110, 255, 0.08)"
                  : "transparent",
              })}
            >
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                color="secondary"
                sx={{ minWidth: "2.5rem", pt: 0.15 }}
              >
                {index + 1}
              </SoftTypography>
              <SoftBox flex={1} minWidth={0}>
                <SoftTypography variant="body2" color="dark" fontWeight="medium">
                  {route}
                </SoftTypography>
                <SoftTypography variant="caption" color="secondary">
                  Pilot: {pilotLabel}
                </SoftTypography>
              </SoftBox>
              {isTargetLeg ? (
                <SoftBadge
                  variant="contained"
                  color="info"
                  badgeContent={isHighlighted ? "This leg" : "Pilot interest"}
                  size="xs"
                  container
                />
              ) : null}
            </SoftBox>
          );
        })}
      </SoftBox>
    </SoftBox>
  );
}

function TaskListRow({
  task,
  onViewMission,
  onViewPilotProfile,
  onAccept,
  onDeny,
  decidingTaskId,
  isLast,
}) {
  const description = buildTaskDescription(task);
  const age = formatTaskAge(getTaskCreatedTime(task));
  const pills = getTaskCategoryPills(task);
  const missionId = taskMissionId(task);
  const pilotProfileId = extractPilotProfileIdFromTask(task);
  const pilotName = extractPilotDisplayNameFromTask(task);
  const canViewPilot = isPilotInterestTask(task);
  const tid = taskId(task);
  const showDecisions = canDecideTask(task);
  const isDeciding = decidingTaskId && tid && decidingTaskId === tid;
  const statusLabel = taskStatus(task);

  return (
    <SoftBox px={3} py={1.25} pb={isLast ? 1.25 : 0}>
      <SoftBox
        display="flex"
        alignItems={{ xs: "flex-start", lg: "center" }}
        justifyContent="space-between"
        gap={2}
        p={2.5}
        sx={(theme) => ({
          width: "100%",
          flexWrap: { xs: "wrap", lg: "nowrap" },
          borderRadius: "1rem",
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: "0 2px 12px rgba(15, 34, 58, 0.05)",
          transition: "box-shadow 0.2s ease, transform 0.2s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(15, 34, 58, 0.08)",
          },
        })}
      >
        <SoftBox display="flex" alignItems="flex-start" gap={1.5} flex={1} minWidth={0}>
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={44}
            height={44}
            borderRadius="md"
            flexShrink={0}
            sx={({ palette, functions: { linearGradient, rgba } }) => {
              const gradient = palette.gradients?.info ?? {};
              return {
                backgroundImage: linearGradient(
                  rgba(gradient.main, 0.15),
                  rgba(gradient.state, 0.08),
                ),
                border: `1px solid ${rgba(gradient.main, 0.2)}`,
                color: palette.info.main,
              };
            }}
          >
            <Icon sx={{ fontSize: "1.35rem !important" }}>notifications_active</Icon>
          </SoftBox>
          <SoftBox flex={1} minWidth={0}>
            <SoftTypography
              variant="button"
              fontWeight="bold"
              color="dark"
              sx={{
                display: "block",
                mb: 0.75,
                lineHeight: 1.55,
                fontSize: "0.9375rem",
              }}
            >
              {description}
            </SoftTypography>
            <TaskRowMeta age={age} pills={pills} statusLabel={statusLabel} />
          </SoftBox>
        </SoftBox>

        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent={{ xs: "flex-start", lg: "flex-end" }}
          flexWrap="wrap"
          gap={0.75}
          flexShrink={0}
          sx={{
            pl: { xs: 0, lg: 1 },
            ml: { xs: 0, lg: "auto" },
            width: { xs: "100%", lg: "auto" },
          }}
        >
          <SoftBox
            display="flex"
            flexWrap="wrap"
            gap={0.75}
            sx={(theme) => ({
              p: 0.75,
              borderRadius: "0.75rem",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(99, 110, 255, 0.04)",
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            {canViewPilot ? (
              <TaskActionButton
                label="View pilot"
                icon="person"
                color="dark"
                onClick={() =>
                  onViewPilotProfile(task, pilotProfileId, pilotName)
                }
              />
            ) : null}
            <TaskActionButton
              label="View Mission"
              icon="flight"
              color="info"
              disabled={!missionId}
              onClick={() => missionId && onViewMission(missionId)}
            />
            {showDecisions ? (
              <>
                <TaskActionButton
                  label="Accept"
                  icon="check"
                  color="success"
                  variant="gradient"
                  disabled={isDeciding}
                  loading={isDeciding}
                  onClick={() => onAccept(task)}
                />
                <TaskActionButton
                  label="Deny"
                  icon="close"
                  color="error"
                  variant="outlined"
                  disabled={isDeciding}
                  onClick={() => onDeny(task)}
                />
              </>
            ) : null}
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

function TasksPage() {
  const { user, isGuest } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusTab, setStatusTab] = useState(TASK_LIST_TAB_KEYS.NEW);
  const [currentPage, setCurrentPage] = useState(1);
  const [pilotProfileModal, setPilotProfileModal] = useState(null);
  const [taskActions, setTaskActions] = useState([]);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [decidingTaskId, setDecidingTaskId] = useState(null);
  const [decisionLegs, setDecisionLegs] = useState([]);
  const [decisionLegsLoading, setDecisionLegsLoading] = useState(false);
  const [decisionAirports, setDecisionAirports] = useState([]);

  const fetchTasks = useCallback(async () => {
      if (isGuest) {
        setTasks([]);
        setLoadingList(false);
        return;
      }
      if (!user?.id_token) {
        setTasks([]);
        setLoadingList(false);
        return;
      }

      setLoadingList(true);
      setError(null);

      try {
        const res = await getTasks(user.id_token);
        const { list } = extractTasksFromServiceResponse(res);
        setTasks(list);
      } catch (err) {
        setTasks([]);
        setError(err?.message || "Failed to load tasks.");
      } finally {
        setLoadingList(false);
      }
    },
    [isGuest, user?.id_token],
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    const task = pendingDecision?.task;
    if (!task) {
      setDecisionLegs([]);
      setDecisionLegsLoading(false);
      setDecisionAirports([]);
      return;
    }

    let cancelled = false;
    const fromTask = extractLegsFromTask(task);
    const missionId = taskMissionId(task);
    const needMissionFetch =
      !legsHaveRouteDetails(fromTask) && Boolean(missionId && user?.id_token);

    setDecisionLegsLoading(needMissionFetch);

    const airportsPromise = user?.id_token
      ? getAirports(user.id_token).catch(() => ({ data: [] }))
      : Promise.resolve({ data: [] });

    const missionPromise = needMissionFetch
      ? getMissionById(missionId, user.id_token)
      : Promise.resolve(null);

    Promise.all([missionPromise, airportsPromise])
      .then(([missionRes, airportsRes]) => {
        if (cancelled) return;

        setDecisionAirports(normalizeAirportsList(airportsRes?.data));

        if (!needMissionFetch) {
          setDecisionLegs(fromTask);
          return;
        }

        let mission = missionRes?.data;
        while (typeof mission === "string") {
          try {
            mission = JSON.parse(mission);
          } catch {
            break;
          }
        }
        const missionLegs = normalizeMissionLegs(mission);
        setDecisionLegs(missionLegs.length ? missionLegs : fromTask);
      })
      .catch(() => {
        if (!cancelled) {
          setDecisionLegs(fromTask);
          setDecisionAirports([]);
        }
      })
      .finally(() => {
        if (!cancelled) setDecisionLegsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pendingDecision?.task, user?.id_token]);

  useGlobalDataRefresh("task", useCallback(() => {
    fetchTasks();
  }, [fetchTasks]));

  useEffect(() => {
    if (!user?.id_token || isGuest) {
      setTaskActions([]);
      return;
    }

    let cancelled = false;
    Promise.all([
      getActions("Task", user.id_token).catch(() => []),
      getActions("Mission", user.id_token).catch(() => []),
    ]).then(([taskList, missionList]) => {
      if (cancelled) return;
      const merged = [...(taskList ?? []), ...(missionList ?? [])];
      const seen = new Set();
      setTaskActions(
        merged.filter((action) => {
          const key = `${action?.type ?? ""}:${action?.subType ?? ""}:${action?.message ?? ""}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [user?.id_token, isGuest]);

  const tabCounts = useMemo(() => {
    const counts = {};
    TASK_LIST_TABS.forEach(({ key }) => {
      counts[key] = tasks.filter((t) => taskMatchesStatusTab(t, key)).length;
    });
    return counts;
  }, [tasks]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusTab, searchQuery]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (!taskMatchesStatusTab(t, statusTab)) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const haystack = [
          buildTaskDescription(t),
          taskStatus(t),
          t.taskCategory,
          t.taskType,
          formatTaskAge(getTaskCreatedTime(t)),
          taskMissionId(t),
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, statusTab, searchQuery]);

  const sortedTasks = useMemo(
    () => sortTasksByNewest(filteredTasks),
    [filteredTasks],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(sortedTasks.length / TASKS_PAGE_SIZE),
  );
  const effectivePage = Math.min(Math.max(1, currentPage), totalPages);

  useEffect(() => {
    if (currentPage !== effectivePage) {
      setCurrentPage(effectivePage);
    }
  }, [currentPage, effectivePage]);

  const paginatedTasks = useMemo(() => {
    const start = (effectivePage - 1) * TASKS_PAGE_SIZE;
    return sortedTasks.slice(start, start + TASKS_PAGE_SIZE);
  }, [sortedTasks, effectivePage]);

  const entriesStart =
    sortedTasks.length === 0 ? 0 : (effectivePage - 1) * TASKS_PAGE_SIZE + 1;
  const entriesEnd = Math.min(
    effectivePage * TASKS_PAGE_SIZE,
    sortedTasks.length,
  );

  const handleViewMission = (missionId) => {
    if (missionId) {
      navigate(`/dashboard/missions/${missionId}`);
    }
  };

  const handleViewPilotProfile = async (task, _profileId, fallbackName) => {
    if (!user?.id_token) return;

    const resolved = await resolvePilotProfileFromTask(
      task,
      user.id_token,
      getMissionById,
    );

    if (!resolved.profileId) {
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "warning",
            title: "Pilot not found",
            message:
              "Could not determine which pilot this task refers to. The pilot may need to be identified in task data.",
          },
        }),
      );
      return;
    }

    setPilotProfileModal({
      task,
      profileId: resolved.profileId,
      fallbackName: resolved.name || fallbackName,
    });
  };

  const handleClosePilotProfile = () => {
    setPilotProfileModal(null);
  };

  const submitTaskDecision = async (task, decision) => {
    const tid = taskId(task);
    const missionId = taskMissionId(task);
    if (!user?.id_token || !tid || !missionId) return;

    setDecidingTaskId(tid);
    try {
      const actionConfig = findTaskDecisionAction(taskActions, decision);
      const pilotProfileId = (
        await resolvePilotProfileFromTask(task, user.id_token, getMissionById)
      ).profileId;
      const input = buildUpdateTaskInput(
        task,
        decision,
        actionConfig,
        pilotProfileId,
      );
      await updateTask(tid, input, user.id_token);
      setTasks((prev) =>
        prev.map((item) => {
          const itemId = taskId(item);
          if (tid && itemId === tid) {
            return {
              ...item,
              status: decision === "accept" ? "Accepted" : "Denied",
              taskStatus: decision === "accept" ? "Accepted" : "Denied",
            };
          }
          return item;
        }),
      );
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "success",
            title: decision === "accept" ? "Task accepted" : "Task denied",
            message:
              decision === "accept"
                ? "The pilot interest was accepted."
                : "The pilot interest was denied.",
          },
        }),
      );
      setPendingDecision(null);
      fetchTasks();
    } catch (err) {
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Action failed",
            message: err?.message ?? "Could not update this task.",
          },
        }),
      );
    } finally {
      setDecidingTaskId(null);
    }
  };

  const handleAcceptTask = (task) => {
    setPendingDecision({ task, decision: "accept" });
  };

  const handleDenyTask = (task) => {
    setPendingDecision({ task, decision: "deny" });
  };

  const showEmptyList = !loadingList && tasks.length === 0 && !error;
  const showNoMatches =
    !loadingList && tasks.length > 0 && sortedTasks.length === 0;

  return (
    <SoftBox py={3}>
      <SoftBox
        mb={3}
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
      >
        <SoftBox>
          <SoftTypography variant="h4" fontWeight="bold" gutterBottom>
            Tasks
          </SoftTypography>
          <SoftTypography variant="body2" color="text" maxWidth={480}>
            Review pilot interest notifications and take action on mission
            tasks assigned to you.
          </SoftTypography>
        </SoftBox>
        <SoftButton
          variant="outlined"
          color="info"
          onClick={() => fetchTasks()}
          disabled={loadingList || isGuest}
          sx={{ borderRadius: "0.75rem" }}
        >
          <Icon sx={{ mr: 0.5 }}>refresh</Icon>
          Refresh
        </SoftButton>
      </SoftBox>

      {error && (
        <SoftBox mb={2}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </SoftBox>
      )}

      {isGuest && (
        <SoftBox mb={2}>
          <Alert severity="info">
            Sign in with a full account to load tasks from the server.
          </Alert>
        </SoftBox>
      )}

      <Card
        sx={{
          overflow: "hidden",
          borderRadius: 2,
          boxShadow: "0 4px 24px rgba(15, 34, 58, 0.06)",
        }}
      >
        {loadingList ? (
          <SoftBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1.5}
            px={3}
            py={6}
          >
            <CircularProgress size={32} color="info" />
            <SoftTypography variant="body2" color="text">
              Loading tasks...
            </SoftTypography>
          </SoftBox>
        ) : showEmptyList ? (
          <SoftBox px={3} py={6} textAlign="center">
            <SoftBox
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              width={64}
              height={64}
              borderRadius="50%"
              mb={2}
              sx={({ palette, functions: { rgba } }) => ({
                backgroundColor: rgba(palette.gradients.info.main, 0.1),
                color: palette.info.main,
              })}
            >
              <Icon sx={{ fontSize: "2rem !important" }}>task_alt</Icon>
            </SoftBox>
            <SoftTypography variant="h6" fontWeight="bold" color="dark" gutterBottom>
              No tasks yet
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              New mission notifications will appear here when pilots show
              interest or action is required.
            </SoftTypography>
          </SoftBox>
        ) : (
          <>
            <SoftBox
              sx={(theme) => ({
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(99, 110, 255, 0.03)",
              })}
            >
              <Tabs
                value={statusTab}
                onChange={(_, value) => setStatusTab(value)}
                sx={{ px: 2, pt: 1.5 }}
              >
                {TASK_LIST_TABS.map(({ key, label }) => (
                  <Tab
                    key={key}
                    value={key}
                    label={`${label} (${tabCounts[key] ?? 0})`}
                  />
                ))}
              </Tabs>
              <SoftBox
                px={3}
                pb={2}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
              >
                <SoftBox
                  sx={{
                    width: 360,
                    maxWidth: "100%",
                    "&:focus-within": {
                      "& .MuiInputBase-root": {
                        border: "2px solid",
                        borderColor: "info.main",
                      },
                    },
                    "& .MuiInputBase-root.Mui-focused": {
                      boxShadow: "none",
                      borderColor: "transparent !important",
                    },
                  }}
                >
                  <SoftInput
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    icon={{ component: "search", direction: "left" }}
                  />
                </SoftBox>
              </SoftBox>
            </SoftBox>

              {showNoMatches ? (
                <SoftBox px={3} py={6} textAlign="center">
                  <Icon sx={{ fontSize: "2.5rem !important", color: "text.secondary", mb: 1 }}>
                    search_off
                  </Icon>
                  <SoftTypography variant="h6" fontWeight="bold" color="dark" gutterBottom>
                    No matching tasks
                  </SoftTypography>
                  <SoftTypography variant="body2" color="text">
                    {searchQuery.trim()
                      ? "Try adjusting your search."
                      : statusTab === TASK_LIST_TAB_KEYS.NEW
                        ? "No new tasks in this tab yet."
                        : statusTab === TASK_LIST_TAB_KEYS.COMPLETED
                          ? "No completed tasks in this tab yet."
                          : "No tasks match this view yet."}
                  </SoftTypography>
                </SoftBox>
              ) : (
                <>
                  <SoftBox component="ul" p={0} m={0} sx={{ listStyle: "none" }}>
                    {paginatedTasks.map((task, index) => (
                      <SoftBox
                        component="li"
                        key={task.taskId ?? task.id ?? index}
                        p={0}
                        m={0}
                      >
                        <TaskListRow
                          task={task}
                          onViewMission={handleViewMission}
                          onViewPilotProfile={handleViewPilotProfile}
                          onAccept={handleAcceptTask}
                          onDeny={handleDenyTask}
                          decidingTaskId={decidingTaskId}
                          isLast={index === paginatedTasks.length - 1}
                        />
                      </SoftBox>
                    ))}
                  </SoftBox>
                  <SoftBox
                    px={3}
                    py={2.5}
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                    sx={{ borderTop: "1px solid", borderColor: "divider" }}
                  >
                    <SoftTypography
                      variant="button"
                      color="secondary"
                      fontWeight="regular"
                    >
                      Showing {entriesStart} to {entriesEnd} of{" "}
                      {sortedTasks.length}{" "}
                      {sortedTasks.length === 1 ? "task" : "tasks"}
                      {searchQuery.trim() ? " (filtered)" : ""}
                    </SoftTypography>
                    {totalPages > 1 ? (
                      <SoftPagination variant="gradient" color="info">
                        <SoftPagination
                          item
                          onClick={() =>
                            setCurrentPage((page) => Math.max(1, page - 1))
                          }
                          disabled={effectivePage <= 1}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                        </SoftPagination>
                        {totalPages <= 7 ? (
                          Array.from({ length: totalPages }, (_, index) => {
                            const page = index + 1;
                            return (
                              <SoftPagination
                                key={page}
                                item
                                active={page === effectivePage}
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </SoftPagination>
                            );
                          })
                        ) : (
                          <SoftTypography variant="button" fontWeight="medium" mx={1}>
                            Page {effectivePage} of {totalPages}
                          </SoftTypography>
                        )}
                        <SoftPagination
                          item
                          onClick={() =>
                            setCurrentPage((page) =>
                              Math.min(totalPages, page + 1),
                            )
                          }
                          disabled={effectivePage >= totalPages}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                        </SoftPagination>
                      </SoftPagination>
                    ) : null}
                  </SoftBox>
                </>
              )}
            </>
          )}
      </Card>

      <ViewPilotProfileModal
        open={Boolean(pilotProfileModal)}
        onClose={handleClosePilotProfile}
        profileId={pilotProfileModal?.profileId}
        idToken={user?.id_token}
        task={pilotProfileModal?.task}
        fallbackName={pilotProfileModal?.fallbackName}
      />

      <Dialog
        open={Boolean(pendingDecision)}
        onClose={() => !decidingTaskId && setPendingDecision(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <SoftBox display="flex" alignItems="center" gap={1}>
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              width={36}
              height={36}
              borderRadius="md"
              sx={({ palette }) => ({
                backgroundColor:
                  pendingDecision?.decision === "accept"
                    ? palette.success.main
                    : palette.error.main,
                color: palette.white.main,
              })}
            >
              <Icon fontSize="small">
                {pendingDecision?.decision === "accept" ? "check" : "close"}
              </Icon>
            </SoftBox>
            {pendingDecision?.decision === "accept" ? "Accept task" : "Deny task"}
          </SoftBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDecision?.decision === "accept"
              ? "Accept this pilot's interest for the mission?"
              : "Deny this pilot's interest for the mission?"}
          </DialogContentText>
          {pendingDecision?.task ? (
            <SoftBox
              mt={2}
              p={2}
              sx={(theme) => ({
                borderRadius: "0.75rem",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.04)"
                    : "rgba(99, 110, 255, 0.05)",
                border: `1px solid ${theme.palette.divider}`,
              })}
            >
              <SoftTypography variant="body2" color="dark" fontWeight="medium">
                {buildTaskDescription(pendingDecision.task)}
              </SoftTypography>
            </SoftBox>
          ) : null}
          {pendingDecision?.task ? (
            <TaskDecisionLegsList
              legs={decisionLegs}
              loading={decisionLegsLoading}
              airports={decisionAirports}
              highlightLegId={extractLegIdFromTask(pendingDecision.task)}
              interestedPilotName={extractPilotDisplayNameFromTask(
                pendingDecision.task,
              )}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton
            variant="text"
            color="secondary"
            onClick={() => setPendingDecision(null)}
            disabled={Boolean(decidingTaskId)}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            color={pendingDecision?.decision === "accept" ? "success" : "error"}
            onClick={() =>
              pendingDecision &&
              submitTaskDecision(pendingDecision.task, pendingDecision.decision)
            }
            disabled={Boolean(decidingTaskId)}
          >
            {decidingTaskId ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                Submitting…
              </>
            ) : pendingDecision?.decision === "accept" ? (
              "Accept"
            ) : (
              "Deny"
            )}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
}

export default TasksPage;
